// =====================================================
// Apple Design Enhancement - Delayed Loading Hook
// Implements the 500ms threshold principle to avoid
// brief loading flickers and improve perceived performance
// =====================================================

import { useState, useEffect, useRef, useCallback } from 'react';

// =====================================================
// Types
// =====================================================

export interface UseDelayedLoadingOptions {
  /**
   * Delay in milliseconds before showing the loading indicator.
   * Default: 500ms (Apple's recommended threshold)
   */
  delay?: number;
  
  /**
   * Minimum time to display the loading indicator once shown.
   * Prevents flash of loading state for loads that complete
   * just after the threshold.
   * Default: 400ms
   */
  minDisplayTime?: number;
}

export interface UseDelayedLoadingReturn {
  /**
   * Whether to show the loading indicator.
   * Will be false if loading completes within the delay threshold.
   * Will be true only after the delay has passed and loading is still in progress.
   */
  showLoading: boolean;
  
  /**
   * The actual loading state passed to the hook.
   * Useful for conditional rendering logic.
   */
  isLoading: boolean;
}

// =====================================================
// Constants
// =====================================================

/** Default delay before showing loading indicator (500ms threshold) */
export const DEFAULT_DELAY = 500;

/** Default minimum display time once loading indicator is shown */
export const DEFAULT_MIN_DISPLAY_TIME = 400;

// =====================================================
// Hook Implementation
// =====================================================

/**
 * A hook that implements the 500ms loading threshold principle.
 * 
 * This hook prevents brief loading flickers by only showing the loading
 * indicator if the loading operation takes longer than the specified delay.
 * Once shown, the loading indicator will remain visible for at least the
 * minimum display time to prevent jarring transitions.
 * 
 * @param isLoading - The current loading state
 * @param options - Configuration options for delay and minimum display time
 * @returns Object containing showLoading and isLoading states
 * 
 * @example
 * ```tsx
 * const { showLoading, isLoading } = useDelayedLoading(fetchingData, {
 *   delay: 500,
 *   minDisplayTime: 400
 * });
 * 
 * return (
 *   <div>
 *     {showLoading ? <Skeleton /> : <Content data={data} />}
 *   </div>
 * );
 * ```
 * 
 * Requirements:
 * - 2.1: WHEN data loading completes within 500ms, THE Loading_Manager SHALL NOT display any loading indicator
 * - 2.2: WHEN data loading exceeds 500ms, THE Loading_Manager SHALL display the appropriate loading indicator
 * - 2.3: THE Loading_Manager SHALL provide a reusable hook (useDelayedLoading) for components to implement this behavior
 * - 2.4: WHEN the loading state changes from loading to loaded, THE Loading_Manager SHALL smoothly transition the content into view
 */
export function useDelayedLoading(
  isLoading: boolean,
  options?: UseDelayedLoadingOptions
): UseDelayedLoadingReturn {
  const {
    delay = DEFAULT_DELAY,
    minDisplayTime = DEFAULT_MIN_DISPLAY_TIME,
  } = options ?? {};
  
  // State to track whether we should show the loading indicator
  const [showLoading, setShowLoading] = useState(false);
  
  // Refs to track timing and cleanup
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const minDisplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadingShownAtRef = useRef<number | null>(null);
  const isLoadingRef = useRef(isLoading);
  
  // Keep isLoading ref in sync
  isLoadingRef.current = isLoading;
  
  // Cleanup function for timers
  const clearTimers = useCallback(() => {
    if (delayTimerRef.current !== null) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    if (minDisplayTimerRef.current !== null) {
      clearTimeout(minDisplayTimerRef.current);
      minDisplayTimerRef.current = null;
    }
  }, []);
  
  useEffect(() => {
    if (isLoading) {
      // Loading started - set up delay timer
      // Only set up the timer if we're not already showing loading
      if (!showLoading) {
        delayTimerRef.current = setTimeout(() => {
          // Only show loading if we're still in loading state
          if (isLoadingRef.current) {
            setShowLoading(true);
            loadingShownAtRef.current = Date.now();
          }
          delayTimerRef.current = null;
        }, delay);
      }
    } else {
      // Loading finished
      // Clear the delay timer if it hasn't fired yet
      if (delayTimerRef.current !== null) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
      
      // If loading indicator is currently shown, ensure minimum display time
      if (showLoading && loadingShownAtRef.current !== null) {
        const elapsedTime = Date.now() - loadingShownAtRef.current;
        const remainingTime = minDisplayTime - elapsedTime;
        
        if (remainingTime > 0) {
          // Wait for minimum display time before hiding
          minDisplayTimerRef.current = setTimeout(() => {
            setShowLoading(false);
            loadingShownAtRef.current = null;
            minDisplayTimerRef.current = null;
          }, remainingTime);
        } else {
          // Minimum display time already elapsed, hide immediately
          setShowLoading(false);
          loadingShownAtRef.current = null;
        }
      }
    }
    
    // Cleanup on unmount or when dependencies change
    return () => {
      clearTimers();
    };
  }, [isLoading, delay, minDisplayTime, showLoading, clearTimers]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);
  
  return {
    showLoading,
    isLoading,
  };
}

export default useDelayedLoading;
